import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, WarningCircle, House } from "@phosphor-icons/react";

/**
 * Landing pubblica raggiunta cliccando su un'emoticon 😞 😐 😄 nella mail di consegna.
 * NON richiede autenticazione: usa il token HMAC firmato passato in query string.
 * URL: /rate/:pid/:score?token=XXXX
 */
const SCORE_META = {
  0:   { emoji: "😞", label: "Insoddisfatto",  color: "bg-red-50 text-red-800 border-red-200"    },
  50:  { emoji: "😐", label: "Neutro",         color: "bg-amber-50 text-amber-800 border-amber-200" },
  100: { emoji: "😄", label: "Soddisfatto",    color: "bg-emerald-50 text-emerald-800 border-emerald-200" },
};

export default function RatePractice() {
  const { pid, score } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [state, setState] = useState({ loading: true, error: null, alreadyRated: false });

  useEffect(() => {
    const s = Number(score);
    if (!token || ![0, 50, 100].includes(s)) {
      setState({ loading: false, error: "Link non valido o incompleto." });
      return;
    }
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${backendUrl}/api/practices/${pid}/rate/${s}?token=${encodeURIComponent(token)}`)
      .then((r) => setState({ loading: false, error: null, alreadyRated: !!r.data?.already_rated }))
      .catch((e) => {
        const detail = e.response?.data?.detail || "Errore durante il salvataggio del voto.";
        setState({ loading: false, error: detail });
      });
  }, [pid, score, token]);

  const meta = SCORE_META[Number(score)] || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 flex items-center justify-center p-6">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl border border-slate-100 p-8 text-center" data-testid="rate-landing">
        {state.loading && (
          <div>
            <div className="text-5xl animate-pulse">⏳</div>
            <p className="mt-4 text-slate-600">Registrazione del tuo voto…</p>
          </div>
        )}
        {!state.loading && state.error && (
          <div>
            <WarningCircle size={56} className="mx-auto text-red-500" weight="fill" />
            <h1 className="mt-4 text-2xl font-bold text-slate-800">Ops!</h1>
            <p className="mt-2 text-sm text-slate-600" data-testid="rate-error">{state.error}</p>
          </div>
        )}
        {!state.loading && !state.error && (
          <div>
            <div className="text-7xl leading-none">{meta.emoji}</div>
            <h1 className="mt-4 font-display text-3xl font-bold text-slate-900" data-testid="rate-success">
              {state.alreadyRated ? "Grazie, avevi già votato!" : "Grazie per il tuo feedback!"}
            </h1>
            <div className={`mt-4 inline-block px-3 py-1.5 rounded-full text-xs font-semibold border ${meta.color}`}>
              Voto registrato: {meta.label}
            </div>
            <p className="mt-6 text-sm text-slate-600 leading-relaxed">
              Il tuo giudizio ci aiuta a migliorare la qualità dei servizi Housetag. Se hai osservazioni
              da condividere, apri la chat interna della pratica per parlare direttamente con l'operatore.
            </p>
          </div>
        )}
        <div className="mt-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-purple-700 hover:text-purple-900"
            data-testid="rate-back-home"
          >
            <House size={16} weight="fill" /> Torna su Housetag
          </Link>
        </div>
      </div>
    </div>
  );
}
